/**
 * Date Range Utilities (Stories 5.8, 5.9)
 *
 * Pure utility functions for date range preset calculation, validation,
 * comparison period calculation, and formatting.
 * All dates use ISO 8601 date-only strings ("YYYY-MM-DD") with UTC-only logic.
 * No external date library — uses native Date + Intl.DateTimeFormat.
 *
 * Story 2.10: History is no longer per-plan gated — all plans get
 * HISTORY_MONTHS (24) months. Functions use the constant directly.
 */

import { HISTORY_MONTHS } from "@provenleap/types";

// ── Types ───────────────────────────────────────────────────────────────

export type ComparisonMode =
  | "previous-period"
  | "same-period-last-month"
  | "same-period-last-year";

export type DateRangePresetKey =
  | "last-7"
  | "last-14"
  | "last-30"
  | "last-90"
  | "this-month"
  | "last-month"
  | "this-quarter"
  | "last-quarter"
  | "custom";

export interface DateRangePreset {
  key: DateRangePresetKey;
  label: string;
}

export interface DateRange {
  start: string;
  end: string;
}

export interface DateRangeValidation {
  valid: boolean;
  error?: string;
}

// ── Constants ───────────────────────────────────────────────────────────

export const DATE_RANGE_PRESETS: DateRangePreset[] = [
  { key: "last-7", label: "Last 7 days" },
  { key: "last-14", label: "Last 14 days" },
  { key: "last-30", label: "Last 30 days" },
  { key: "last-90", label: "Last 90 days" },
  { key: "this-month", label: "This Month" },
  { key: "last-month", label: "Last Month" },
  { key: "this-quarter", label: "This Quarter" },
  { key: "last-quarter", label: "Last Quarter" },
  { key: "custom", label: "Custom" },
];

// ── Helpers ─────────────────────────────────────────────────────────────

/** Returns today's date as an ISO 8601 date-only string (UTC). */
function getToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Subtracts N days from a date string and returns ISO date string. */
function subtractDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/** Gets the first day of the month for a given date. */
function firstOfMonth(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

/** Gets the last day of the month for a given year/month (0-indexed month). */
function lastOfMonth(year: number, month: number): string {
  // month is 0-indexed: 0=Jan, 11=Dec
  // day 0 of next month = last day of current month
  const d = new Date(Date.UTC(year, month + 1, 0));
  return d.toISOString().slice(0, 10);
}

/** Gets the quarter (1-4) for a 0-indexed month. */
function getQuarter(month: number): number {
  return Math.floor(month / 3) + 1;
}

/** Gets the first month (0-indexed) of a quarter (1-4). */
function quarterStartMonth(quarter: number): number {
  return (quarter - 1) * 3;
}

// ── Public API ──────────────────────────────────────────────────────────

/**
 * Calculates the start and end dates for a given preset.
 * All calculations use UTC to avoid timezone issues.
 */
export function calculatePresetRange(
  presetKey: DateRangePresetKey,
  today?: string
): DateRange {
  const t = today ?? getToday();
  const d = new Date(t + "T00:00:00Z");

  switch (presetKey) {
    case "last-7":
      return { start: subtractDays(t, 6), end: t };
    case "last-14":
      return { start: subtractDays(t, 13), end: t };
    case "last-30":
      return { start: subtractDays(t, 29), end: t };
    case "last-90":
      return { start: subtractDays(t, 89), end: t };
    case "this-month":
      return { start: firstOfMonth(t), end: t };
    case "last-month": {
      const prevMonth = d.getUTCMonth() === 0 ? 11 : d.getUTCMonth() - 1;
      const prevYear =
        d.getUTCMonth() === 0
          ? d.getUTCFullYear() - 1
          : d.getUTCFullYear();
      return {
        start: `${prevYear}-${String(prevMonth + 1).padStart(2, "0")}-01`,
        end: lastOfMonth(prevYear, prevMonth),
      };
    }
    case "this-quarter": {
      const q = getQuarter(d.getUTCMonth());
      const qStart = quarterStartMonth(q);
      return {
        start: `${d.getUTCFullYear()}-${String(qStart + 1).padStart(2, "0")}-01`,
        end: t,
      };
    }
    case "last-quarter": {
      const currentQ = getQuarter(d.getUTCMonth());
      let prevQ = currentQ - 1;
      let year = d.getUTCFullYear();
      if (prevQ === 0) {
        prevQ = 4;
        year -= 1;
      }
      const qStartMonth = quarterStartMonth(prevQ);
      const qEndMonth = qStartMonth + 2;
      return {
        start: `${year}-${String(qStartMonth + 1).padStart(2, "0")}-01`,
        end: lastOfMonth(year, qEndMonth),
      };
    }
    case "custom":
      // Custom returns empty — caller provides the dates
      return { start: "", end: "" };
  }
}

/**
 * Checks if a preset's date range falls within the history window.
 * A preset is allowed if its start date >= the earliest allowed date.
 */
export function isPresetWithinPlanLimit(
  presetKey: DateRangePresetKey,
  today?: string
): boolean {
  if (presetKey === "custom") return true; // Custom is always shown (dates validated separately)
  const { start } = calculatePresetRange(presetKey, today);
  const earliest = getEarliestAllowedDate(today);
  return start >= earliest;
}

/**
 * Returns the earliest date allowed by the history window (HISTORY_MONTHS).
 */
export function getEarliestAllowedDate(
  today?: string
): string {
  const t = today ?? getToday();
  const d = new Date(t + "T00:00:00Z");
  d.setUTCMonth(d.getUTCMonth() - HISTORY_MONTHS);
  return d.toISOString().slice(0, 10);
}

/**
 * Validates a date range against constraints.
 * Returns { valid: true } or { valid: false, error: "..." }.
 */
export function validateDateRange(
  start: string,
  end: string,
  today?: string
): DateRangeValidation {
  const t = today ?? getToday();

  // Check both dates are present
  if (!start || !end) {
    return { valid: false, error: "Both start and end dates are required." };
  }

  // Check valid ISO 8601 format (YYYY-MM-DD)
  const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoRegex.test(start) || !isoRegex.test(end)) {
    return { valid: false, error: "Dates must be in YYYY-MM-DD format." };
  }

  // Check dates are actually valid
  const startDate = new Date(start + "T00:00:00Z");
  const endDate = new Date(end + "T00:00:00Z");
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return { valid: false, error: "Invalid date values." };
  }
  // Roundtrip check: catch impossible dates like Feb 30 (JS silently rolls forward)
  if (startDate.toISOString().slice(0, 10) !== start || endDate.toISOString().slice(0, 10) !== end) {
    return { valid: false, error: "Invalid date values." };
  }

  // Check start <= end
  if (start > end) {
    return { valid: false, error: "Start date must be before or equal to end date." };
  }

  // Check end date <= today
  if (end > t) {
    return { valid: false, error: "End date cannot be in the future." };
  }

  // Check start is within history window
  const earliest = getEarliestAllowedDate(t);
  if (start < earliest) {
    return {
      valid: false,
      error: `Date range exceeds the ${HISTORY_MONTHS}-month history window. Earliest allowed date: ${formatDateRangeDisplay(earliest, earliest).split(" \u2013 ")[0]}.`,
    };
  }

  return { valid: true };
}

/**
 * Formats a date range for display.
 * E.g., "Jan 28 – Feb 27, 2026"
 * Uses Intl.DateTimeFormat with en-US locale matching existing patterns.
 */
export function formatDateRangeDisplay(start: string, end: string): string {
  if (!start || !end) return "";

  const startDate = new Date(start + "T00:00:00Z");
  const endDate = new Date(end + "T00:00:00Z");

  const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();

  const fmtWithYear = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

  const fmtNoYear = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  const startStr = sameYear
    ? fmtNoYear.format(startDate)
    : fmtWithYear.format(startDate);
  const endStr = fmtWithYear.format(endDate);

  return `${startStr} \u2013 ${endStr}`;
}

// ── Comparison Period Utilities (Story 5.9) ──────────────────────────────

/**
 * Shifts a date by monthDelta months with clamping to avoid JS Date silent rollover.
 * E.g., Mar 31 shifted -1 month → Feb 28 (NOT Mar 3).
 */
function shiftMonthClamped(dateStr: string, monthDelta: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  const originalDay = d.getUTCDate();
  d.setUTCMonth(d.getUTCMonth() + monthDelta);
  // If day changed (e.g., 31→3 because Feb has 28 days), clamp to last day of target month
  if (d.getUTCDate() !== originalDay) {
    d.setUTCDate(0); // Sets to last day of previous month (which is our target month)
  }
  return d.toISOString().slice(0, 10);
}

/**
 * Shifts a date by yearDelta years with leap-year clamping.
 * E.g., Feb 29 2028 shifted -1 year → Feb 28, 2027.
 */
function shiftYearClamped(dateStr: string, yearDelta: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  const originalDay = d.getUTCDate();
  d.setUTCFullYear(d.getUTCFullYear() + yearDelta);
  if (d.getUTCDate() !== originalDay) {
    d.setUTCDate(0);
  }
  return d.toISOString().slice(0, 10);
}

/**
 * Calculates the comparison date range for a given primary range and comparison mode.
 *
 * - "previous-period": same duration, ending the day before primary start
 * - "same-period-last-month": shift both dates back 1 month (with clamping)
 * - "same-period-last-year": shift both dates back 1 year (with clamping)
 */
export function calculateComparisonRange(
  start: string,
  end: string,
  mode: ComparisonMode
): DateRange {
  switch (mode) {
    case "previous-period": {
      const startDate = new Date(start + "T00:00:00Z");
      const endDate = new Date(end + "T00:00:00Z");
      const durationMs = endDate.getTime() - startDate.getTime();
      const durationDays = Math.round(durationMs / (24 * 60 * 60 * 1000));
      const compEnd = subtractDays(start, 1);
      const compStart = subtractDays(compEnd, durationDays);
      return { start: compStart, end: compEnd };
    }
    case "same-period-last-month":
      return {
        start: shiftMonthClamped(start, -1),
        end: shiftMonthClamped(end, -1),
      };
    case "same-period-last-year":
      return {
        start: shiftYearClamped(start, -1),
        end: shiftYearClamped(end, -1),
      };
  }
}

/**
 * Checks if the comparison start date falls within the history window.
 */
export function isComparisonWithinPlanLimit(
  comparisonStart: string,
  today?: string
): boolean {
  const earliest = getEarliestAllowedDate(today);
  return comparisonStart >= earliest;
}

/**
 * Returns a warning object if the comparison period exceeds the history window.
 * Returns null if within limits.
 */
export function getMinPlanForComparison(
  comparisonStart: string,
  today?: string
): { months: number } | null {
  if (isComparisonWithinPlanLimit(comparisonStart, today)) {
    return null;
  }
  return { months: HISTORY_MONTHS };
}

export const COMPARISON_MODE_LABELS: Record<ComparisonMode, string> = {
  "previous-period": "Previous period",
  "same-period-last-month": "Same period last month",
  "same-period-last-year": "Same period last year",
};
